import { inject, Injectable, signal } from '@angular/core';
import { FiledTarget } from './mail-upload/mail-upload.models';
import { MailService } from './mail-upload/mail.service';

/**
 * Where the open mail has been filed, as the record in its custom properties
 * says, plus what was filed since. The one place the pane asks that, whether it
 * decides which page to open on or lists the targets.
 */
@Injectable({
  providedIn: 'root'
})
export class FilingsService {
  private readonly _mailService = inject(MailService);
  private readonly _filings = signal<FiledTarget[]>([]);

  /** Newest first. */
  public readonly filings = this._filings.asReadonly();

  /**
   * Reads the record of the mail open now. Reading is a round trip, and a
   * pinned pane can move on to the next mail across it: then the result is
   * about a mail no longer open and is dropped, and the next load follows.
   *
   * A failed read rejects, and leaves the mail with no filings rather than
   * with those of the mail before.
   */
  public async load(): Promise<FiledTarget[]> {
    const itemId = this._mailService.getCurrentItemId();
    let filings: FiledTarget[] = [];

    try {
      filings = itemId === null ? [] : await this._mailService.getCurrentMailFilings();
      return filings;
    } finally {
      if (this._mailService.getCurrentItemId() === itemId) {
        this._filings.set(filings);
      }
    }
  }

  /**
   * Adds a filing of the open mail: in memory at once, so the confirmation can
   * list it, then to the record on the mail. Only the write can fail.
   */
  public record(target: FiledTarget): Promise<void> {
    this._filings.update((filings) => [target, ...filings]);

    return this._mailService.recordCurrentMailFiling(target);
  }
}
